import { Link, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { logout } from '../../store/slices/authSlice';
import { toggleSidebar } from '../../store/slices/uiSlice';
import ThemeToggle from '../UI/ThemeToggle';
import AntiguaFlag from '../UI/AntiguaFlag';

const Header = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleToggleDrawer = () => {
    dispatch(toggleSidebar());
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="header-bg sticky top-0 shadow-sm border-b border-[var(--color-neutral-border)] z-40">
      <div className="container mx-auto px-4">
        {/* Top Banner */}
        <div className="flex items-center gap-3 py-3 border-b border-[var(--color-neutral-border)]">
          <div className="w-8 h-6 flex-shrink-0">
            <AntiguaFlag width={32} height={21} className="rounded-sm" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[var(--color-header-link-primary)]">
              ANTIGUA & BARBUDA
            </h1>
            <p className="text-sm text-[var(--color-header-link-secondary)]">
              Citizens Participation Portal
            </p>
          </div>
          {isAuthenticated && (
            <div className="text-sm text-[var(--color-header-link-secondary)]">
              Welcome, {user?.profile?.nickname ? user.profile.nickname : user?.profile?.displayName || user?.email?.split('@')[0] || 'Citizen'}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="nav-color flex items-center justify-between py-3">
          {/* Menu button for mobile drawer */}
          <button
            onClick={handleToggleDrawer}
            className="lg:hidden p-2 text-[var(--color-header-link-primary)] hover:bg-[var(--color-neutral-100)] rounded-lg transition-colors mr-2"
            aria-label="Open navigation menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {/* Desktop navigation - hidden on mobile, shown in drawer */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                isActive('/') || isActive('/home')
                  ? 'text-[var(--color-header-link-active)] border-b-2 border-[var(--color-header-link-active)] pb-1'
                  : 'text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)]'
              }`}
            >
              Home
            </Link>
            <Link
              to="/projects"
              className={`text-sm font-medium transition-colors ${
                isActive('/projects')
                  ? 'text-[var(--color-header-link-active)] border-b-2 border-[var(--color-header-link-active)] pb-1'
                  : 'text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)]'
              }`}
            >
              Community Projects
            </Link>
            <Link
              to="/vote"
              className={`text-sm font-medium transition-colors ${
                isActive('/vote')
                  ? 'text-[var(--color-header-link-active)] border-b-2 border-[var(--color-header-link-active)] pb-1'
                  : 'text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)]'
              }`}
            >
              Vote
            </Link>
                <Link
                  to="/dashboard"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/dashboard')
                      ? 'text-[var(--color-header-link-active)] border-b-2 border-[var(--color-header-link-active)] pb-1'
                      : 'text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)]'
                  }`}
                >
                  My Earnings
                </Link>
                {isAuthenticated && (
                <Link
                  to="/projects/create"
                  className="text-sm font-medium text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)] transition-colors"
                >
                  Create Project
                </Link>
                )}
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors ${
                isActive('/about')
                  ? 'text-[var(--color-header-link-active)] border-b-2 border-[var(--color-header-link-active)] pb-1'
                  : 'text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)]'
              }`}
            >
              About
            </Link>
          </nav>
          <div className="flex items-center gap-4">
              <Link
                to="/wallet"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  isActive('/wallet')
                    ? 'bg-[var(--color-header-link-active)]/20 text-[var(--color-header-link-active)]'
                    : 'text-[var(--color-header-link-primary)] hover:bg-[var(--color-neutral-100)]/10'
                }`}
                title="My Wallet"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="text-sm font-semibold">
                  <span className="text-xs opacity-75 mr-1">XCD</span>
                  {(user?.wallet?.balance ?? 1250.75).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Link>
            {isAuthenticated ? (
              <button
                onClick={handleLogout}
                className="text-sm text-[var(--color-header-link-primary)] hover:text-[var(--color-error-600)] transition-colors"
              >
                Sign Out
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-[var(--color-header-link-primary)] hover:text-[var(--color-header-link-active)] transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-[var(--color-header-link-active)] text-white rounded hover:bg-[var(--color-brand-700)] transition-colors text-sm"
                >
                  Register
                </Link>
              </>
            )}
            <ThemeToggle />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
